import * as ExcelJS from 'exceljs';
import moment from 'moment';
import { UserModel } from '../../app/modules/auth/models/UserModel';
import { CustomField, CustomFieldValue, SettingMultiMeetings, SettingMultiMerchants, SettingMultiPipelines } from '../../interfaces/Settings';
import { User } from '../../interfaces/User';
import { UserCompany } from '../../interfaces/UserCompany';
import { MultiSimpleMeeting, SimpleMeeting } from '../../interfaces/Meeting';
import { MultiSimpleMerchant, SimpleMerchant } from '../../interfaces/Merchant';
import { MultiSimplePipeline, SimplePipeline } from '../../interfaces/Pipeline';
import { currencyFormatter } from '../general';
import { Label } from '../../interfaces/Label';
import { MultiSimpleProduct, SimpleProduct } from '../../interfaces/Product';

let globalUsedWorksheetNames = new Set<string>();

export const addWorkSheetInfo = async (
    worksheetInfo: ExcelJS.Worksheet,
    source: string,
    nameFile: string,
    nameInfo: string,
    nameHeader: any,
    user: UserModel
) => {
    worksheetInfo.protect('mjioerh3uiqWHEIOPHQWIOPEJHDWBH', {
        formatColumns: true,
        autoFilter: true,
    });
    worksheetInfo.mergeCells('A1', 'B1');
    worksheetInfo.getCell('A1').value = nameHeader.info.fileInformation;
    worksheetInfo.getCell('A2').value = nameHeader.info.downloader;
    worksheetInfo.getCell('B2').value = `${user.data.name}`;
    worksheetInfo.getCell('A3').value = nameHeader.info.downloadTime;
    worksheetInfo.getCell('B3').value = `${moment().format('DD MMM YYYY HH:mm')}`;
    worksheetInfo.getCell('A4').value = nameHeader.info.type;
    worksheetInfo.getCell('B4').value = nameInfo;

    worksheetInfo.getRow(1).font = {
        name: 'Arial Black',
        size: 16,
    };
    worksheetInfo.getRow(1).alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
    };
    worksheetInfo.getCell('A4').font = {
        color: {
            argb: 'FFFFFFFF',
        },
    };
    worksheetInfo.getCell('B4').font = {
        color: {
            argb: 'FFFFFFFF',
        },
    };

    worksheetInfo.columns = [
        {
            width: 25,
        },
        {
            width: 30,
        },
    ];
};

export const createWorkbook = () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Looyal';
    workbook.lastModifiedBy = 'Looyal';
    workbook.created = new Date();
    workbook.modified = new Date();
    return workbook;
};

export const createWorksheet = (workbook: ExcelJS.Workbook, sheetName: string) => {
    const worksheet = workbook.addWorksheet(sheetName);
    return worksheet;
};

export const setColumnWidths = (worksheet: ExcelJS.Worksheet, source: string, fields: string[], isFeatureLabelEnabled?: boolean, selectMultiPipeline?: SettingMultiPipelines, typeAction: string = 'insert', isCompanyVaporesia?: boolean) => {
    const sheetName = worksheet.name;
    const colWidths = Object.keys(fields).map(() => ({ width: 25 }));
    const colStyles = Object.keys(fields).map(() => ({ numFmt: '@' }));

    if (worksheet.name === 'Satuan Produk (Opsional)') {
        colWidths[1] = { width: 43 };
        colWidths[2] = { width: 36 };
    }


    if (!isFeatureLabelEnabled && sheetName === 'Sheet1' && (source === 'template-merchant') && typeAction === 'update') {
        colWidths[3] = { width: 0 };
    }

    if (!isFeatureLabelEnabled && sheetName === 'Sheet1' && (source === 'template-pipeline' || source === 'template-meeting' || source === 'template-merchant') && typeAction === 'insert') {
        colWidths[2] = { width: 0 };
    }

    if (!selectMultiPipeline?.isFeatureCogsEnabled && sheetName === 'Form Produk' && source === 'template-pipeline') {
        colWidths[2] = { width: 0 };
    }

    if (sheetName === 'Sheet1' && source === 'template-merchant' && isCompanyVaporesia) {
        const index = typeAction === 'insert' ? 5 : 6;
        colWidths[index] = { width: 0 };
    }

    worksheet.columns = Object.keys(fields).map((field, index) => ({
        ...colWidths[index],
        style: colStyles[index],
    }));
};

export const styleColumns = (worksheet: ExcelJS.Worksheet, source: string, indexRow: number) => {
    if (indexRow === 1) {
        worksheet.getRow(indexRow).alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        };
        worksheet.getRow(indexRow).font = {
            name: 'Arial Black',
            size: 12,
            bold: true,
        };
        worksheet.getRow(indexRow).eachCell(function (cell) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: {
                    argb: 'F6991D',
                },
            };
        });
    } else if (indexRow === 2) {
        worksheet.getRow(indexRow).eachCell(function (cell) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: {
                    argb: 'F8B219'
                },
            }
        });
    }
};

export const exportWorkbook = async (
    workbook: ExcelJS.Workbook,
    fileName: string,
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        setIsLoading?.(false);
    });
};


export const getColumnIndexFromLetter = (letter: string) => {
    let column = 0;
    for (let i = 0; i < letter.length; i++) {
        column *= 26;
        column += letter.charCodeAt(i) - 65 + 1;
    }
    return column - 1; // 0-based
};

export const getColumnExcelNameLetter = (
    currentColumn: number,
    key: string,
    typeAction: string,
    type: string,
    param: any,
    singleOptionSheets?: any,
) => {
    const columnLetter = getExcelColumnName(currentColumn);
    const worksheetName = singleOptionSheets?.[key];
    let dataLength = 0;

    if (typeAction === 'update') {
        dataLength = type === 'error' ? param.listErrorUpdate.length : param.dataTables.length;
    }

    return {
        columnLetter,
        worksheetName,
        dataLength,
    };
};

export function getColumnLetter(colIndex: number): string {
    let letter = '';
    while (colIndex > 0) {
        const modulo = (colIndex - 1) % 26;
        letter = String.fromCharCode(65 + modulo) + letter;
        colIndex = Math.floor((colIndex - 1) / 26);
    }
    return letter;
}

export function getExcelColumnName(index: number): string {
    let columnName = '';
    index += 1;
    while (index > 0) {
        let remainder = (index - 1) % 26;
        columnName = String.fromCharCode(65 + remainder) + columnName;
        index = Math.floor((index - 1) / 26);
    }
    return columnName;
}

export const generateWorksheetNameColumnCustomField = (customFields: CustomFieldValue, workbook: ExcelJS.Workbook, intl: any) => {
    let usedNames = new Set<string>();
    let baseWorksheetName = `CF ${customFields?.name.replace(/\s+/g, ' ')}`;
    let worksheetName = baseWorksheetName;
    let counter = 1;

    while (usedNames.has(worksheetName.toLowerCase())) {
        worksheetName = `${baseWorksheetName} (${counter})`;
        counter++;
    }

    usedNames.add(worksheetName.toLowerCase());

    let singleOptionSheet = workbook.addWorksheet(worksheetName);
    if (customFields.type !== 'multiple' && customFields.type !== 'multipleOption' && customFields.type !== 'pipeline' && customFields.type !== 'meeting' && customFields.type !== 'merchant' && customFields.type !== 'user' && customFields.type !== 'product') {
        singleOptionSheet.protect('mjioerh3uiqWHEIOPHQWIOPEJHDWBH', {
            formatColumns: true,
            autoFilter: true,
        });
        // singleOptionSheet.getCell('A1').value = `${customFields.name}`;
    }
    return {
        worksheetName,
        singleOptionSheet,
    }
}

export const addNumberFormatting = (startColumn: string, customFields: any, typeAction: string, type: string, param: any, worksheet: ExcelJS.Worksheet) => {
    let currentColumn = getColumnIndexFromLetter(startColumn)
    Object.entries(customFields).forEach(([key, field]: [string, any]) => {
        if (field.type === 'number') {
            const { columnLetter, dataLength } = getColumnExcelNameLetter(currentColumn, key, typeAction, type, param);
            for (let i = 3; i <= (typeAction === 'update' ? dataLength + 2 : 1000); i++) {
                const cell = worksheet.getCell(`${columnLetter}${i}`);
                cell.alignment = { horizontal: 'right' };
                cell.numFmt = '#,##0;[Red]-#,##0';
            }
        }
        currentColumn++;
    });

    return currentColumn;
};

export interface simpleCustomFieldsMasterProps {
    pipelines: MultiSimplePipeline
    meetings: MultiSimpleMeeting
    merchants: MultiSimpleMerchant
    users: Record<string, User[]>;
    products: MultiSimpleProduct
}

interface CreateSingleOptionWorksheetsProps {
    customFields: CustomField
    workbook: ExcelJS.Workbook
    simpleCustomFieldsMaster: simpleCustomFieldsMasterProps
    user: UserModel
    intl: any
    source: string
    multiMerchants?: SettingMultiMerchants[]
    selectMultiMeeting?: SettingMultiMeetings
    merchants?: SimpleMerchant[]
}

export const createSingleOptionWorksheets = ({ customFields, workbook, simpleCustomFieldsMaster, user, intl, source, multiMerchants, selectMultiMeeting, merchants }: CreateSingleOptionWorksheetsProps) => {
    let singleOptionSheets: { [key: string]: string } = {};
    // Reset the global set of used worksheet names for each new workbook
    globalUsedWorksheetNames.clear();

    Object.entries(customFields).forEach(([key, field]) => {
        if (field.type === 'option') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);
            if (Array.isArray(field.value) && typeof field.value[0] === 'string') {
                (field.value as string[]).forEach((option: string, index: number) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = option;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'pipeline') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);

            if (simpleCustomFieldsMaster?.pipelines && simpleCustomFieldsMaster?.pipelines[key] &&
                simpleCustomFieldsMaster?.pipelines[key].length > 0) {

                simpleCustomFieldsMaster?.pipelines[key].forEach((pipeline, index) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = `${pipeline.name} / ${pipeline.nominal} / ${moment(pipeline.dateCreated).format('DD MMM YYYY')}`;
                });
            }

            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'meeting') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);
            if (simpleCustomFieldsMaster?.meetings && simpleCustomFieldsMaster?.meetings[key] && simpleCustomFieldsMaster?.meetings[key].length > 0) {
                simpleCustomFieldsMaster?.meetings[key].forEach((meeting: SimpleMeeting, index: number) => {
                    const splitData = meeting?.id?.split('-');
                    const subString = splitData?.[splitData.length - 1].substring(7, splitData?.[splitData.length - 1].length);
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus} / ${subString}`;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'merchant') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);
            if (simpleCustomFieldsMaster?.merchants && simpleCustomFieldsMaster.merchants[key] && simpleCustomFieldsMaster?.merchants[key].length > 0) {
                simpleCustomFieldsMaster?.merchants[key].forEach((merchant: SimpleMerchant, index: number) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = `${merchant.name} / ${merchant.phone || '-'}`;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'user') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);
            if (simpleCustomFieldsMaster?.users && simpleCustomFieldsMaster.users[key] && simpleCustomFieldsMaster?.users[key].length > 0) {
                simpleCustomFieldsMaster?.users[key].forEach((user: User, index: number) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = user.name;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'multiple' || field.type === 'multipleOption') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);

            if (Array.isArray(field.value) && typeof field.value[0] === 'string') {
                (field.value as string[]).forEach((option: string, index: number) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = option;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        } else if (field.type === 'product') {
            const { worksheetName, singleOptionSheet } = generateWorksheetNameColumnCustomField(field as CustomFieldValue, workbook, intl);
            if (simpleCustomFieldsMaster?.products && simpleCustomFieldsMaster.products[key] && simpleCustomFieldsMaster?.products[key].length > 0) {
                simpleCustomFieldsMaster?.products[key].forEach((product: SimpleProduct, index: number) => {
                    singleOptionSheet.getCell(`A1`).value = `Daftar (CF) ${field.name}`;
                    singleOptionSheet.getCell(`A${index + 2}`).value = product.name;
                });
            }
            // Menggunakan key sebagai kunci untuk mengidentifikasi worksheet secara unik
            singleOptionSheets[key] = worksheetName;
        }
    });
    return singleOptionSheets;
};

export const getListIdLabel = (dataLabel: Label[], rowData: string[]) => {
    const listIdLabel = dataLabel?.filter((label: Label) => rowData?.includes(label.name));
    return listIdLabel?.map((label: Label) => label.id);
}

export const getListIdParticipant = (dataParticipant: UserCompany[], rowData: string) => {
    const normalizedRowData = rowData?.split(',').map((name: string) => name?.trim().toLowerCase()) || [];

    const listIdParticipant = dataParticipant?.filter((participant: UserCompany) => {
        const normalizedParticipantName = participant.ownerName?.trim().toLowerCase();
        return normalizedRowData.includes(normalizedParticipantName);
    });

    return listIdParticipant?.map((participant: UserCompany) => participant.id);
}

export const validateDateTime = (dateTimeValue: string, intl: any, type: string = 'default') => {
    let startDate, endDate, dateStart;
    const fieldsErrors: string[] = [];
    const now = moment();

    if (dateTimeValue.includes(' - ')) {
        // Format dengan waktu "YYYY-MM-DD HH:mm - YYYY-MM-DD HH:mm"
        [startDate, endDate] = dateTimeValue.split(' - ');

        if (!moment(startDate, 'YYYY-MM-DD HH:mm', true).isValid() || !moment(endDate, 'YYYY-MM-DD HH:mm', true).isValid()) {
            fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.DATE_TIME_INVALID' }));
            return fieldsErrors;
        }

        if (type === 'report') {
            // Untuk 'report', waktu mulai harus sebelum hari ini
            if (moment(startDate, 'YYYY-MM-DD HH:mm').isAfter(now)) {
                fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.REPORT_DATE_MUST_BE_BEFORE_TODAY' }));
            }
        } else {
            // Periksa apakah waktu mulai sudah lewat
            if (moment(startDate, 'YYYY-MM-DD HH:mm').isBefore(now)) {
                fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.END_DATE_TIME_BEFORE_START' }));
            }
        }
    } else {
        // Format tanpa waktu "YYYY-MM-DD"
        dateStart = dateTimeValue;

        if (!moment(dateStart, 'YYYY-MM-DD', true).isValid()) {
            fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.DATE_TIME_INVALID' }));
            return fieldsErrors;
        }

        if (type === 'report') {
            // Untuk 'report', tanggal harus sebelum hari ini
            if (moment(dateStart, 'YYYY-MM-DD').isAfter(now.startOf('day'))) {
                fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.REPORT_DATE_MUST_BE_BEFORE_TODAY' }));
            }
        } else {
            // Periksa apakah tanggal sudah lewat
            if (moment(dateStart, 'YYYY-MM-DD').isBefore(now.startOf('day'))) {
                fieldsErrors.push(intl.formatMessage({ id: 'FORM.VALIDATION.END_DATE_TIME_BEFORE_START' }));
            }
        }
    }

    return fieldsErrors;
}

export const formatCustomField = (row: any, field: string, type: string, user: UserModel, source?: string) => { // use in meeting, and report termin export excel
    let valueInfoCustomFields: any[] = [];
    if (source === 'meeting') {
        valueInfoCustomFields = (row.info_custom_fields && row.info_custom_fields[field]) ? row.info_custom_fields[field] : [];
    } else if (source === 'reportTermin') {
        valueInfoCustomFields = (row.customFieldTermin && row.customFieldTermin[field]) ? row.customFieldTermin[field] : [];
    }
    let value;
    if (source === 'meeting') {
        value = row.custom_fields[field];
    } else if (source === 'reportTermin') {
        value = row.customFieldTermin[field];
    }

    if (type === 'meeting') return valueInfoCustomFields.length > 0 ? valueInfoCustomFields.map((item: any) => `${item.merchant_name} / ${moment(item.date_meet).format('DD MMM YYYY')} / ${item.meeting_status}`).join(', ') : '-';
    else if (type === 'pipeline') return valueInfoCustomFields.length > 0 ? valueInfoCustomFields.map((item: any) => `${item.name} / ${item.nominal} / ${moment(item.dateCreated).format('DD MMM YYYY')}`).join(', ') : '-';
    else if (type === 'merchant') return valueInfoCustomFields.length > 0 ? valueInfoCustomFields.map((item: any) => item.merchant_name).join(', ') : '-';
    else if (type === 'user') {
        return valueInfoCustomFields.length > 0 ? valueInfoCustomFields.map((item: any) => item.name).join(', ') : '-';
    }
    else if (type === 'invoice_reminder') {
        const parsedValue = JSON.parse(value);
        return [
            parsedValue.invoice_url,
            parsedValue.date_due ? moment(parsedValue.date_due).format('DD MMM YYYY') : '-',
        ];
    }
    else if (type === 'date') {
        return value ? moment(value).format('YYYY-MM-DD') : '-';
    }
    else if (type === 'dateTime') {
        return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
    }
    else if (type === 'multiple' || type === 'multipleOption') {
        return value ? value.toString().split(',').join(', ') : '-';
    }
    else if (type === 'images' || type === 'files' || type === 'videos') {
        return value ? value.toString().split(',').join('\n ') : '-';
    }
    else if (type === 'serial') {
        return value ? value.join('') : '-';
    }
    else if (type === 'dateTimeRange') {
        return value
            ? (typeof value === 'string'
                ? value.split(',').map((item: any) => moment(item).format('YYYY-MM-DD HH:mm')).join(' - ')
                : Array.isArray(value)
                    ? value.map((item: any) => moment(item).format('YYYY-MM-DD HH:mm')).join(' - ')
                    : value)
            : '-';
    }
    else if (type === 'time') {
        return value
            ? (typeof value === 'string'
                ? value.split(',').map((item: any) => moment(item).format('HH:mm')).join(' - ')
                : Array.isArray(value)
                    ? value.map((item: any) => moment(item).format('HH:mm')).join(' - ')
                    : value)
            : '-';
    } else if (type === 'product') return valueInfoCustomFields.length > 0 ? valueInfoCustomFields.map((item: any) => item.product_name).join(', ') : '-';
    else {
        return value && Object.keys(value).length > 0 ? value : '-';
    }
};